/**
 * useProductionForecast Hook
 * US-013D: State management for production forecasting
 */

import { useState, useCallback, useEffect } from 'react'
import {
  generateProductionPlan,
  getProductionPlan,
  getDailyTasks,
  updatePlanEntry as updatePlanEntryApi,
  groupPlanByWeek,
  groupPlanByStrain,
  getUpcomingMilestones
} from './productionForecastService'

/**
 * Hook for managing production forecast state
 *
 * @param {Object} options
 * @param {boolean} [options.autoLoad=true] - Automatically load plan on mount
 * @param {number} [options.weeksAhead=26] - Weeks to load
 * @returns {Object} Hook state and functions
 */
export function useProductionForecast(options = {}) {
  const { autoLoad = true, weeksAhead = 26 } = options

  // Plan state
  const [plan, setPlan] = useState([])
  const [planByWeek, setPlanByWeek] = useState(new Map())
  const [planByStrain, setPlanByStrain] = useState(new Map())
  const [upcomingMilestones, setUpcomingMilestones] = useState([])

  // Tasks state
  const [tasks, setTasks] = useState([])
  const [tasksSummary, setTasksSummary] = useState(null)

  // Loading states
  const [loading, setLoading] = useState(false)
  const [tasksLoading, setTasksLoading] = useState(false)
  const [generating, setGenerating] = useState(false)

  // Error states
  const [error, setError] = useState(null)
  const [tasksError, setTasksError] = useState(null)

  // Filters
  const [strainFilter, setStrainFilter] = useState(null)
  const [statusFilter, setStatusFilter] = useState(['planned'])

  // ==========================================================================
  // Load Plan
  // ==========================================================================

  const loadPlan = useCallback(async (filterOptions = {}) => {
    setLoading(true)
    setError(null)

    try {
      const result = await getProductionPlan({
        strain_id: filterOptions.strain_id ?? strainFilter,
        status: filterOptions.status ?? statusFilter,
        start_week: filterOptions.start_week,
        end_week: filterOptions.end_week
      })

      const entries = result.data || []
      setPlan(entries)

      // Update derived state
      setPlanByWeek(groupPlanByWeek(entries))
      setPlanByStrain(groupPlanByStrain(entries))
      setUpcomingMilestones(getUpcomingMilestones(entries, 14))

      return result
    } catch (err) {
      console.error('[useProductionForecast] Load error:', err)
      setError(err.message || 'Failed to load production plan')
      throw err
    } finally {
      setLoading(false)
    }
  }, [strainFilter, statusFilter])

  // ==========================================================================
  // Generate Plan
  // ==========================================================================

  const generatePlan = useCallback(async (genOptions = {}) => {
    setGenerating(true)
    setError(null)

    try {
      const result = await generateProductionPlan({
        weeks_ahead: genOptions.weeks_ahead ?? weeksAhead,
        strain_id: genOptions.strain_id,
        force_regenerate: genOptions.force_regenerate ?? false
      })

      // Reload plan after generation
      await loadPlan()

      return result
    } catch (err) {
      console.error('[useProductionForecast] Generate error:', err)
      setError(err.message || 'Failed to generate production plan')
      throw err
    } finally {
      setGenerating(false)
    }
  }, [weeksAhead, loadPlan])

  // ==========================================================================
  // Update Entry
  // ==========================================================================

  const updatePlanEntry = useCallback(async (planId, updates) => {
    setError(null)

    try {
      const result = await updatePlanEntryApi(planId, updates)

      // Update local state
      setPlan(prev => prev.map(entry =>
        entry.id === planId ? { ...entry, ...updates } : entry
      ))

      return result
    } catch (err) {
      console.error('[useProductionForecast] Update error:', err)
      setError(err.message || 'Failed to update plan entry')
      throw err
    }
  }, [])

  // ==========================================================================
  // Load Tasks
  // ==========================================================================

  const loadTasks = useCallback(async (taskOptions = {}) => {
    setTasksLoading(true)
    setTasksError(null)

    try {
      const result = await getDailyTasks({
        date: taskOptions.date,
        days_ahead: taskOptions.days_ahead ?? 7
      })

      setTasks(result.tasks || [])
      setTasksSummary(result.summary || null)

      return result
    } catch (err) {
      console.error('[useProductionForecast] Tasks error:', err)
      setTasksError(err.message || 'Failed to load daily tasks')
      throw err
    } finally {
      setTasksLoading(false)
    }
  }, [])

  // ==========================================================================
  // Filter Handlers
  // ==========================================================================

  const handleStrainFilterChange = useCallback((strainId) => {
    setStrainFilter(strainId || null)
  }, [])

  const handleStatusFilterChange = useCallback((statuses) => {
    setStatusFilter(statuses || ['planned'])
  }, [])

  // ==========================================================================
  // Refresh
  // ==========================================================================

  const refresh = useCallback(async () => {
    await Promise.all([
      loadPlan(),
      loadTasks()
    ])
  }, [loadPlan, loadTasks])

  // ==========================================================================
  // Auto-load on mount
  // ==========================================================================

  useEffect(() => {
    if (autoLoad) {
      loadPlan()
      loadTasks()
    }
  }, [autoLoad, loadPlan, loadTasks])

  // ==========================================================================
  // Reload when filters change
  // ==========================================================================

  useEffect(() => {
    if (!autoLoad) return

    loadPlan({
      strain_id: strainFilter,
      status: statusFilter
    })
  }, [strainFilter, statusFilter, autoLoad, loadPlan])

  // ==========================================================================
  // Return
  // ==========================================================================

  return {
    // Plan data
    plan,
    planByWeek,
    planByStrain,
    upcomingMilestones,

    // Tasks data
    tasks,
    tasksSummary,

    // Loading states
    loading,
    tasksLoading,
    generating,

    // Error states
    error,
    tasksError,

    // Filters
    strainFilter,
    statusFilter,
    setStrainFilter: handleStrainFilterChange,
    setStatusFilter: handleStatusFilterChange,

    // Actions
    loadPlan,
    generatePlan,
    updatePlanEntry,
    loadTasks,
    refresh
  }
}

export default useProductionForecast
